/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-await-in-loop */
import { AnyAction } from 'redux';
import { v4 as uuidv4 } from 'uuid';

// Data Layer
import {
  AUTOPX_DISCOUNT_ID,
  ELECTRONIC_DISCOUNT_ID,
} from 'data-layer-cr/dist/constants/discounts';
import {
  completed,
  processFailed,
} from 'data-layer-cr/dist/functions/paymentCodes';
import {
  getDiscountedTotal,
  getDiscountTotalById,
} from 'data-layer-cr/dist/functions/quotations';
import { Quotation, ServerResponse } from 'data-layer-cr/dist/models';
import { setRequestStatus } from 'data-layer-cr/dist/network';
import {
  FatalRetryError,
  NetworkResponse,
} from 'data-layer-cr/dist/network/NetworkTypes';
import { YappyUrl } from 'data-layer-cr/dist/responseObjects';
import { overwriteCarPolicies } from 'data-layer-cr/dist/slices/carPolicies/slice';
import {
  setCode as setPaymentErrorCode,
  setDescription as setPaymentErrorDescription,
} from 'data-layer-cr/dist/slices/paymentError/slice';
import { timeout } from 'data-layer-cr/dist/utils';

// Constants
import {
  AUTO_PX,
  CAR_BRAND,
  CAR_MODEL,
  CAR_YEAR,
  COVERAGE_TYPE,
  DEDUCTIBLE_OPTION,
  ERROR_CODE,
  ERROR_DESCRIPTION,
  PREMIUM,
  setUserValues,
  SUMMARY_EMISSION_STEP,
  trackCurrentStep,
  trackEvent,
} from 'src/analytics';
import { EMISSION_SIGNED } from 'src/constants/emissionSignature';
import ERROR_DETAILS from 'src/constants/errorDetailCode';
import { PAYMENT_FAILED } from 'src/constants/paymentCodes';

// Store
import { overwriteCoverageCatalog } from 'src/store/coverageCatalog/slice/coverageCatalogSlice';
import { overwriteCreditorCatalog } from 'src/store/creditorCatalog/slice';
import { overwriteInspectionForm } from 'src/store/inspectionForm/slice';
import { overwriteLegalEntityCatalog } from 'src/store/legalEntityCatalog/slice';
import { overwritePerson } from 'src/store/person/slice/personSlice';
import { overwriteProfile } from 'src/store/profile/slice';
import { overwriteQuotation } from 'src/store/quotation/slice/quotationSlice';
import { overwriteStepper } from 'src/store/steppers/slice';

// Models
import { Fraction } from 'src/models/helperTypes/FractionationType';
import PaymentIssuer from 'src/models/helperTypes/PaymentIssuer';
import {
  makeEmissionRequest,
  makeEvicertiaRequest,
  makeYappyPaymentPayload,
} from 'src/models/requestTypes/creators/makeEmissionRequest';
import { PersistedEmissionData } from 'src/models/requestTypes/YappyEmissionRequest';
import { EmissionConsulting } from 'src/models/responseTypes/EmissionConsultingResponse';
import PolicySignInfo from 'src/models/responseTypes/PolicySignInfo';

import profiles from 'src/constants/profileTypes';

import { AppThunk, AsyncAppThunk, AsyncDispatch } from '../../ThunkTypes';

import {
  overwriteEmissionPolicy,
  PaymentDataFields,
  TotalsPayload,
  updateAdditionalDocumentACHBase64,
  updateAdditionalDocumentACHFileName,
  updateAnnualPayments,
  updateDataFields,
  updateEmissionErrorCode,
  updateEmissionErrorDetail,
  updateEmissionErrorDetailDescription,
  updateEmissionOrderId,
  updateEmissionRequestIndicator,
  updateEmissionStatusCode,
  updateEvicertiaLink,
  updateExclusionDate,
  updateFinalBeneficiary,
  updateFractionation,
  updateIdentificationBase64,
  updateIdentificationFileName,
  updateIdentificationOptionalBase64,
  updateIdentificationOptionalFileName,
  updateIsPaymentRetry,
  updateLegalBeneficiaries,
  updateMonthlyPayments,
  updateNewCarInvoiceBase64,
  updateNewCarInvoiceFilename,
  updateNewDataFields,
  updateNewPayerDataFields,
  updatePaymentAmount,
  updatePaymentDayNumber,
  updatePaymentIssuer,
  updatePaymentIssuerSource,
  updatePaymentQuantity,
  updatePaymentQuantityType,
  updatePaymentType,
  updatePolicyStartDate,
  updateShowPaymentAlert,
  updateShowSignAlert,
  updateTotalPayments,
} from '../slice/emissionPolicySlice';

import { setSelectedPackageOption } from '../../quotation/slice/quotationSlice';
import {
  setForcePaymentPage,
  setIsOnPackageSelection,
  setIsStartedEmission,
  updateCurrentStepEmission,
} from '../../steppers/slice';

// Models

import EmissionDataFields, {
  LegalBeneficiary,
} from '../../../models/helperTypes/EmissionDataFields';
import FinalBeneficiary from '../../../models/helperTypes/FinalBeneficiary';
import EmissionPaymentStatus from '../../../models/responseTypes/EmissionPaymentStatus';
import TotalsQuotationResponse from '../../../models/responseTypes/TotalsQuotationResponse';

// Methods
import getMaskedMimeType from '../../../functions/getMaskedMimeType';
import { getIndexFromPackageOption } from '../../../functions/individualOptionHelpers';
import prepareBase64 from '../../../functions/prepareBase64';
import {
  getCorrectEventProperties,
  isItAutoPx,
  RETRYPAYMENT,
} from './helperMethods';

import processRequest from '../../../services/processRequest';
import {
  CREATE_PRICING,
  REQUEST_CONSULT_EMISSION_DATA,
  REQUEST_EVICERTIA_DATA,
  REQUEST_FAILURE_STATUS,
  REQUEST_FRACTIONATION,
  REQUEST_PERSISTED_DATA,
  REQUEST_UPDATE_EMISSION_DATA,
  REQUEST_UPLOAD_CEDULA_FILE,
  REQUEST_UPLOAD_CEDULA_OPTIONAL_FILE,
  REQUEST_UPLOAD_EMISSION_FILES,
  REQUEST_UPLOAD_PAYMENT_FILE,
  REQUEST_UPSELLING,
  REQUEST_YAPPY_PAYMENT,
  UPDATE_ANNUAL_PAYMENT,
  UPDATE_MONTHLY_PAYMENT,
} from '../requestIdentifiers';

export const genericPaymentErrorMessage =
  'Error al procesar el pago, intente nuevamente o seleccione otro método';
/** Since we want to know if the emission creation failed, we have to track
 * the event in here.
 */

function trackSummaryEvent(
  quotation: Quotation,
  paymentFields: PaymentDataFields,
  error?: any,
): void {
  const { isAutoPx, packageOption, productId } = quotation;
  const { type, paymentQuantityType } = paymentFields;
  const {
    total,
    coverages,
    package: unitPackage,
  } = quotation.units[packageOption - 1];
  const { carModel, carBrand, carYear } = quotation.summary;
  const { grandTotal } = getDiscountedTotal({
    total,
    quotation,
    paymentType: type,
    paymentQuantityType,
  });

  let customEventProperties: { [key: string]: string } = {
    [CAR_MODEL]: carModel,
    [CAR_BRAND]: carBrand,
    [CAR_YEAR]: carYear,
    [PREMIUM]: grandTotal.toString(),
    [COVERAGE_TYPE]: productId,
    [DEDUCTIBLE_OPTION]: unitPackage.description,
    [AUTO_PX]: isItAutoPx(isAutoPx),
  };

  customEventProperties = getCorrectEventProperties(
    error,
    customEventProperties,
    coverages,
  );

  trackEvent({
    eventName: SUMMARY_EMISSION_STEP,
    eventProperties: customEventProperties,
  });

  trackCurrentStep(SUMMARY_EMISSION_STEP);
}

/**
 * Method passed as a fail condition for retrying payment status request
 */
export function incompleteStatus(data: EmissionPaymentStatus): boolean {
  return !completed(data.statusCode);
}

/**
 * Method passed as a fatal fail condition for retrying payment status request
 */
export function failedStatus(data: EmissionPaymentStatus): boolean {
  return processFailed(data.statusCode);
}

/**
 * Since there are 2 action creators that require this logic, I have encapsulated it in a standalone method to
 * pervent code duplication issues.
 *
 * @param e - The error generated by axios.
 * @param dispatch - The dispatch instance connected to our store.
 */
function handleCheckFailError(
  e: any,
  dispatch: AsyncDispatch,
  errorDetail = '',
): void {
  if (e && e instanceof FatalRetryError) {
    const { statusCode, description } = e.response;
    dispatch(updateEmissionErrorCode(statusCode));
    dispatch(updateIsPaymentRetry(true));
    dispatch(setPaymentErrorCode(statusCode));
    dispatch(updateEmissionErrorDetail(statusCode));
    dispatch(updateEmissionErrorDetailDescription(description as string));
    dispatch(setPaymentErrorDescription(description));
  } else {
    /**
     * Since we need to display the error screen on any code other than
     * the failed/denied payment attempts, force the error code to a simple
     * "error" string
     */
    dispatch(updateEmissionErrorCode('error'));
    dispatch(updateEmissionErrorDetail(errorDetail));
  }
}

export const uploadEmissionImage = async (
  base64File: string,
  quotationId: number,
  fileSystemName: string,
): Promise<string> => {
  const mime = getMaskedMimeType(base64File);
  const extension = mime.includes('image') ? '.png' : '.pdf';

  const data = await processRequest<{
    content: string;
  }>({
    url: `/PolicyEmission/${quotationId}/uploadfiles`,
    method: 'POST',
    data: {
      id: '',
      name: `${fileSystemName}${extension}`,
      value: prepareBase64(base64File),
      mime: `${mime}`,
    },
  });

  return data.content;
};

/**
 * Updates the local identification with the provided base64 representation
 *
 * @param base64Image - The base64 representation of the identification
 */
export const setIdentificationBase64 =
  (fileName: string, base64Image: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const mime = getMaskedMimeType(base64Image);
    const extension = mime.includes('image') ? '.png' : '.pdf';
    const newFileName =
      fileName.substring(0, fileName.lastIndexOf('.')) + extension;

    const currentState = getState();
    const { quotation } = currentState.quotation;

    // Set request status to 'requesting'
    dispatch(updateIdentificationBase64(base64Image));
    dispatch(updateIdentificationFileName(newFileName));
    dispatch(setRequestStatus(REQUEST_UPLOAD_CEDULA_FILE, 'requesting'));

    // Perform the network request
    try {
      const dataUrl =
        base64Image !== ''
          ? await uploadEmissionImage(base64Image, quotation.id, 'cedula')
          : '';

      dispatch(updateIdentificationBase64(dataUrl));
      dispatch(updateIdentificationFileName(newFileName));

      return dispatch(setRequestStatus(REQUEST_UPLOAD_CEDULA_FILE, 'success'));
    } catch (e) {
      dispatch(updateIdentificationBase64(''));
      dispatch(updateIdentificationFileName(''));
      handleCheckFailError(e, dispatch);
      return dispatch(setRequestStatus(REQUEST_UPLOAD_CEDULA_FILE, 'failure'));
    }
  };

/**
 * Updates the local identification with the provided base64 representation
 *
 * @param base64Image - The base64 representation of the identification
 */
export const setIdentificationOptionalBase64 =
  (fileName: string, base64Image: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const mime = getMaskedMimeType(base64Image);
    const extension = mime.includes('image') ? '.png' : '.pdf';
    const newFileName =
      fileName.substring(0, fileName.lastIndexOf('.')) + extension;

    const currentState = getState();
    const { quotation } = currentState.quotation;

    // Set request status to 'requesting'
    dispatch(updateIdentificationOptionalBase64(base64Image));
    dispatch(updateIdentificationOptionalFileName(newFileName));
    dispatch(
      setRequestStatus(REQUEST_UPLOAD_CEDULA_OPTIONAL_FILE, 'requesting'),
    );

    // Perform the network request
    try {
      const dataUrl =
        base64Image !== ''
          ? await uploadEmissionImage(
              base64Image,
              quotation.id,
              'cedula-optional',
            )
          : '';

      dispatch(updateIdentificationOptionalBase64(dataUrl));
      dispatch(updateIdentificationOptionalFileName(newFileName));

      return dispatch(
        setRequestStatus(REQUEST_UPLOAD_CEDULA_OPTIONAL_FILE, 'success'),
      );
    } catch (e) {
      dispatch(updateIdentificationOptionalBase64(''));
      dispatch(updateIdentificationOptionalFileName(''));
      handleCheckFailError(e, dispatch);
      return dispatch(
        setRequestStatus(REQUEST_UPLOAD_CEDULA_OPTIONAL_FILE, 'failure'),
      );
    }
  };

/**
 * Resets values for cedula files
 *
 * @param fileId - file name id
 */
export const resetCedulaFiles =
  (fileId: string): AppThunk =>
  (dispatch): void => {
    if (fileId === 'cedula') {
      dispatch(updateIdentificationBase64(''));
      dispatch(updateIdentificationFileName(''));
    } else {
      dispatch(updateIdentificationOptionalBase64(''));
      dispatch(updateIdentificationOptionalFileName(''));
    }
  };

/**
 * Updates the local new car data with the provided base64 representation
 *
 * @param base64Image - The base63 representation of the new car invoice
 */
export const setCarInvoiceBase64 =
  (fileName: string, base64Image: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const mime = getMaskedMimeType(base64Image);
    const extension = mime.includes('image') ? '.png' : '.pdf';
    const newFileName =
      fileName.substring(0, fileName.lastIndexOf('.')) + extension;

    const currentState = getState();
    const { quotation } = currentState.quotation;

    // Set request status to 'requesting'
    dispatch(updateNewCarInvoiceBase64(base64Image));
    dispatch(updateNewCarInvoiceFilename(newFileName));
    dispatch(setRequestStatus(REQUEST_UPLOAD_EMISSION_FILES, 'requesting'));

    // Perform the network request
    try {
      const quotationProfileType =
        currentState.quotation.quotation.quotationProfile.type ?? '';

      if (quotationProfileType === profiles.RETENCION) {
        const dataUrl =
          base64Image !== ''
            ? await uploadEmissionImage(
                base64Image,
                quotation.id,
                'certificado_poliza',
              )
            : '';

        dispatch(updateNewCarInvoiceBase64(dataUrl));
        dispatch(updateNewCarInvoiceFilename(newFileName));
      } else {
        const dataUrl =
          base64Image !== ''
            ? await uploadEmissionImage(base64Image, quotation.id, 'proforma')
            : '';

        dispatch(updateNewCarInvoiceBase64(dataUrl));
        dispatch(updateNewCarInvoiceFilename(newFileName));
      }

      return dispatch(
        setRequestStatus(REQUEST_UPLOAD_EMISSION_FILES, 'success'),
      );
    } catch (e) {
      dispatch(updateNewCarInvoiceBase64(''));
      dispatch(updateNewCarInvoiceFilename(''));
      handleCheckFailError(e, dispatch);
      return dispatch(
        setRequestStatus(REQUEST_UPLOAD_EMISSION_FILES, 'failure'),
      );
    }
  };

/**
 * Updates emission data fields, which represents the customer's data
 *
 * @param dataFields - New values for emission data fields
 */
export const setDataFields =
  (dataFields: EmissionDataFields): AppThunk =>
  (dispatch): void => {
    dispatch(updateDataFields(dataFields));
  };

export const setNewDataFields =
  (dataFields: Partial<EmissionDataFields>): AppThunk =>
  (dispatch): void => {
    dispatch(updateNewDataFields(dataFields));
  };

export const setNewPayerDataFields =
  (dataFields: Partial<PaymentIssuer>): AppThunk =>
  (dispatch): void => {
    dispatch(updateNewPayerDataFields(dataFields));
  };

/** Updates Final Beneficiary */
export const setFinalBeneficiary =
  (finalBeneficiary: FinalBeneficiary): AppThunk =>
  (dispatch): void => {
    dispatch(updateFinalBeneficiary(finalBeneficiary));
  };
/**
 * Updates the payment type with the provided value
 *
 * @param type - The new payment type
 */
export const setPaymentType =
  (type: string): AppThunk =>
  (dispatch): void => {
    dispatch(updatePaymentType(type));
  };

/**
 * Updates the payment issuer source with the provided value.
 *
 * @param source - The new source
 */
export const setPaymentIssuer =
  (source: string, newIssuer: PaymentIssuer): AppThunk =>
  (dispatch): void => {
    dispatch(updatePaymentIssuerSource(source));
    dispatch(updatePaymentIssuer(newIssuer));
  };

/**
 * Batch all required changes.
 * Basically a convenience method, since all the form fields are editted at once because
 * they are in the same screen
 *
 * @param newValues - The new values to apply to the store
 */
export const updateAllValues =
  (newValues: PaymentDataFields): AppThunk =>
  (dispatch): void => {
    dispatch(updatePaymentType(newValues.type));
    dispatch(updatePaymentQuantity(newValues.paymentQuantity));
    dispatch(updatePaymentAmount(newValues.paymentAmount));
    dispatch(updatePaymentQuantityType(newValues.paymentQuantityType));
    dispatch(updatePolicyStartDate(newValues.policyStartDate));
    dispatch(updatePaymentDayNumber(newValues.paymentDayNumber));
  };

export const clearEmissionPaymentError =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(updateEmissionErrorCode(''));
    dispatch(updateEmissionErrorDetail(''));
    dispatch(updateShowSignAlert(false));
    dispatch(updateShowPaymentAlert(true));
    dispatch(updateEmissionRequestIndicator('initial'));
  };

export const closePaymentAlert =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(updateShowPaymentAlert(false));
  };

export const closeSignatureAlert =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(updateShowSignAlert(false));
  };

/**
 * Updates the additional document for ACH Payments
 *
 * @param base64Image - The base64 representation of the identification
 */
export const setAdditionalDocumentACH =
  (fileName: string, base64Image: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const mime = getMaskedMimeType(base64Image);
    const extension = mime.includes('image') ? '.png' : '.pdf';
    const newFileName =
      fileName.substring(0, fileName.lastIndexOf('.')) + extension;

    const currentState = getState();
    const { quotation } = currentState.quotation;

    // Set request status to 'requesting'
    dispatch(updateAdditionalDocumentACHBase64(base64Image));
    dispatch(updateAdditionalDocumentACHFileName(newFileName));
    dispatch(setRequestStatus(REQUEST_UPLOAD_PAYMENT_FILE, 'requesting'));

    // Perform the network request
    try {
      const dataUrl =
        base64Image !== ''
          ? await uploadEmissionImage(base64Image, quotation.id, 'achDocument')
          : '';

      dispatch(updateAdditionalDocumentACHBase64(dataUrl));
      dispatch(updateAdditionalDocumentACHFileName(newFileName));

      return dispatch(setRequestStatus(REQUEST_UPLOAD_PAYMENT_FILE, 'success'));
    } catch (e) {
      dispatch(updateAdditionalDocumentACHBase64(''));
      dispatch(updateAdditionalDocumentACHFileName(''));
      handleCheckFailError(e, dispatch);
      return dispatch(setRequestStatus(REQUEST_UPLOAD_PAYMENT_FILE, 'failure'));
    }
  };

/**
 * Updates the selected total payments
 * @param newSelectedTotals - Is either monthly or annual selected
 * @returns
 */
export const setTotalPayments =
  (newSelectedTotals: TotalsPayload): AppThunk =>
  (dispatch): void => {
    dispatch(updateTotalPayments(newSelectedTotals));
  };

/**
 * Call the service for calculate the totals quotation
 *
 * @param hashQuotation - The quotation's unique hash
 * @param quantity - The quantity fee for calculate totals
 */
export const requestUpdateTotals =
  (
    hashQuotation: string,
    quantity: number,
    identifier: string,
  ): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    dispatch(setRequestStatus(identifier, 'requesting'));

    try {
      if (
        identifier !== UPDATE_ANNUAL_PAYMENT &&
        identifier !== UPDATE_MONTHLY_PAYMENT
      ) {
        throw new Error('Identifier not found');
      }
      // extract package option from store's current state
      const { packageOption } = getState().quotation.quotation;

      const { data } = await processRequest<TotalsQuotationResponse>({
        method: 'POST',
        url: `Quotation/${hashQuotation}/price?&quantity=${quantity}&packageOption=${packageOption}`,
      });

      const payload: TotalsPayload = {
        paymentTotal: data.grandTotal,
        electronicPaymentTotal: getDiscountTotalById(
          data.discounts,
          ELECTRONIC_DISCOUNT_ID,
        ),
        autoPxPaymentTotal: getDiscountTotalById(
          data.discounts,
          AUTOPX_DISCOUNT_ID,
        ),
      };

      if (identifier === UPDATE_ANNUAL_PAYMENT) {
        dispatch(updateAnnualPayments(payload));
      } else {
        dispatch(updateMonthlyPayments(payload));
      }

      return dispatch(setRequestStatus(identifier, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(identifier, 'failure'));
    }
  };

export function checkSignStatusIncomplete(
  data: ServerResponse<PolicySignInfo>,
): boolean {
  // eslint-disable-next-line no-extra-boolean-cast
  return !Boolean(data.data.signatureUrl);
}

export function checkSignStatusFatal(): boolean {
  return false;
}

/**
 * Requests eviceritia to get the uniqueId and saves the emission for later use.
 * .
 */
export const requestSignEmission =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    // current state
    const currentState = getState();
    const { emissionData } = makeYappyPaymentPayload(currentState);

    // Properties fro evicertia submit to get the uniqueId and redirect URL
    const profileId = currentState.quotation.quotation.quotationProfile.id;
    const evicertiaRequest = makeEvicertiaRequest(emissionData, profileId);

    dispatch(setRequestStatus(REQUEST_EVICERTIA_DATA, 'requesting'));
    // Perform the network request
    try {
      await processRequest<any>({
        method: 'POST',
        url: 'PolicyEmission/evicert/signsubmit',
        data: evicertiaRequest,
      });

      const signInfo = await processRequest<ServerResponse<PolicySignInfo>>({
        method: 'GET',
        url: `PolicyEmission/emissionsignurl/${emissionData.urlSalt}`,
        retryProps: {
          maxAttempts: 10,
          customFail: checkSignStatusIncomplete,
          customFatalFail: checkSignStatusFatal,
        },
      });

      trackSummaryEvent(
        currentState.quotation.quotation,
        currentState.emissionPolicy.paymentFields,
      );
      dispatch(updateEvicertiaLink(signInfo.data.signatureUrl));
      return dispatch(setRequestStatus(REQUEST_EVICERTIA_DATA, 'success'));
    } catch (e) {
      handleCheckFailError(e, dispatch, ERROR_DETAILS.evicertia);
      return dispatch(setRequestStatus(REQUEST_EVICERTIA_DATA, 'failure'));
    }
  };

/**
 * Requests the emission creation in the backend.
 */
export const requestEmissionCreation =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    // Each attempt will have a unique id to prevent previous attempts from polluting the result
    const uniqueId = uuidv4();

    // current state
    const currentState = getState();

    // Properties for the registry
    const { inspectionRequired } = currentState.coverageCatalog.productConfig;
    const { vehicleRegistryImage } = currentState.inspectionForm;

    // Request creation
    const { creditCardData, achData, claveData } = currentState;
    const { quotation } = currentState.quotation;
    const { banks, creditCardBanks } = currentState.creditorCatalog;
    const { textValues } = currentState.inspectionForm;
    const { Email: email } = currentState.profile.profile;
    const userName = currentState.profile.profile.Name;
    const userId = currentState.profile.profile.IdUser;
    const userLicense = currentState.profile.currentLicense;
    const userRole = currentState.profile.currentRole;

    const formState = currentState.emissionPolicy;

    const request = makeEmissionRequest({
      quotation,
      formState,
      bankList: banks,
      inspectionTextValues: textValues,
      uniqueRequestId: uniqueId,
      creditCardData,
      achData,
      sessionEmails: [email],
      sessionUserName: userName,
      sessionUserId: userId,
      sessionLicense: userLicense,
      sessionRole: userRole,
      creditCardBankList: creditCardBanks,
      claveData,
      cities: currentState.legalEntityCatalog.cities,
      townships: currentState.legalEntityCatalog.townships,
    });

    if (!inspectionRequired && vehicleRegistryImage !== '') {
      const mime = getMaskedMimeType(vehicleRegistryImage);
      const extension = mime.includes('image') ? '.png' : '.pdf';
      request.aditionalDocuments.push({
        id: '',
        name: `vehicle-registry-image${extension}`,
        content: prepareBase64(vehicleRegistryImage),
        mime,
      });
    }

    request.acceptInformedConsent = true;

    // Set request status to 'requesting'
    dispatch(updateEmissionRequestIndicator('requesting'));

    // Perform the network request
    try {
      /**
       * Try to create the emission
       */
      await processRequest<string>({
        method: 'POST',
        url: 'Quotation/RequestEmission',
        data: request,
      });

      /**
       * Then check if the payment was applied.
       *
       * To check if the payment completed successfully, we need to query the getCurrentStatus
       * endpoint repeatedly until:
       * - We detect a "payment approved" code (or any code after that)
       * - We detect a "payment failed" or "payment denied", in which case:
       *   - drop the request and perform a fatal error.
       *   Note: we do this distinction via the "tolerance" variable. when the tolerance if
       *   (n - 1) in comparison to the maxAttempts, customFail and customFatalFail will
       *   basically be treated equally.
       * - We run out of retries, which will generate an error.
       */
      const { id } = quotation;

      await processRequest<EmissionPaymentStatus>({
        method: 'GET',
        url: `PolicyEmission/getCurrentStatus?quotationId=${id}&emissionRequestId=${uniqueId}`,
        retryProps: {
          maxAttempts: 10,
          customFail: incompleteStatus,
          customFatalFail: failedStatus,
        },
      });

      /**
       * There is a small delay between the "activation" and whether the pdf file is ready.
       * The issue here is that there is currently no way to actually check if the pdf was
       * already created, since the POLICY_ACTIVATION status does not ensure this, and not
       * all emission processes pass through the PAYMENT_APPLICATION_SUCCESS|FAILURE. The only
       * solution right now that doesn't involve changing code in the backend is to manually
       * force a timeout of 5 seconds, when hopefully the pdf should already have generated.
       */
      await timeout(5000);
      dispatch(updateEmissionErrorCode(''));
      trackSummaryEvent(quotation, currentState.emissionPolicy.paymentFields);
      return dispatch(updateEmissionRequestIndicator('success'));
    } catch (e) {
      handleCheckFailError(e, dispatch);
      trackSummaryEvent(
        quotation,
        currentState.emissionPolicy.paymentFields,
        e,
      );
      return dispatch(updateEmissionRequestIndicator('failure'));
    }
  };

/** Pay Policy With Yappy */
export const requestYappyPayment =
  (): AppThunk<Promise<void>> =>
  async (dispatch, getState): Promise<void> => {
    dispatch(setRequestStatus(REQUEST_YAPPY_PAYMENT, 'requesting'));
    const payload = makeYappyPaymentPayload(getState());
    const { quotation } = getState().quotation;
    const { paymentFields } = getState().emissionPolicy;

    try {
      const { data } = await processRequest<ServerResponse<YappyUrl>>({
        method: 'POST',
        url: 'Payments/yappy',
        data: payload,
      });
      trackSummaryEvent(quotation, paymentFields);
      dispatch(setRequestStatus(REQUEST_YAPPY_PAYMENT, 'success'));

      window.location.href = data.url;
    } catch (e) {
      dispatch(updateEmissionErrorCode(PAYMENT_FAILED));
      dispatch(setPaymentErrorCode(PAYMENT_FAILED));
      dispatch(setPaymentErrorDescription(genericPaymentErrorMessage));
      dispatch(setRequestStatus(REQUEST_YAPPY_PAYMENT, 'failure'));
      trackSummaryEvent(quotation, paymentFields, e);
    }
  };

/** Validates if a policy is emitted */
export const validateEmissionStatus =
  (orderId: string): AsyncAppThunk =>
  async (dispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(CREATE_PRICING, 'requesting'));
    const maxAttempt = 10;
    let currAttempt = 0;

    while (maxAttempt > currAttempt) {
      try {
        // delay between validations
        await timeout(currAttempt * 1000);

        const { data } = await processRequest<
          NetworkResponse<EmissionPaymentStatus>
        >({
          method: 'GET',
          url: `PolicyEmission/status/${orderId}`,
        });
        dispatch(updateEmissionStatusCode(data.statusCode));

        // Sucessful validation
        if (completed(data.statusCode)) {
          return dispatch(setRequestStatus(CREATE_PRICING, 'success'));
        }

        // exit early if detected failed status
        if (failedStatus(data)) {
          // If emission fails, add error properties to Summary
          trackEvent({
            eventName: SUMMARY_EMISSION_STEP,
            eventProperties: {
              [ERROR_CODE]: data.statusCode,
              [ERROR_DESCRIPTION]: data.description,
            },
          });
          return dispatch(setRequestStatus(CREATE_PRICING, 'failure'));
        }
      } catch (e) {
        /**
         * In general, network errors arrive here, in which case we simply advance to the next
         * iteration through the finally block
         *
         * Pending: logging features.
         */
      }
      currAttempt += 1;
    }

    // If policy status couldn't be found, track it, meaning there is something wrong with the service.
    trackEvent({
      eventName: SUMMARY_EMISSION_STEP,
      eventProperties: {
        [ERROR_CODE]: '404',
        [ERROR_DESCRIPTION]: 'Policy Status Not Found',
      },
    });

    // reached maxAttempts and no valid status detected
    return dispatch(setRequestStatus(CREATE_PRICING, 'failure'));
  };

/** Repopulate redux if yappy payment was cancelled or failed. */
export const repopulateRedux =
  (orderId: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_PERSISTED_DATA, 'requesting'));
    try {
      const { data } = await processRequest<
        ServerResponse<{ salesEmissionRedux: PersistedEmissionData }>
      >({
        method: 'GET',
        url: `PolicyEmission/emissiondata/${orderId}`,
      });

      const {
        emissionPolicy,
        profile,
        quotation,
        creditorCatalog,
        person,
        inspectionForm,
        coverageCatalog,
        stepper,
        carPolicies,
      } = data.salesEmissionRedux;

      setUserValues(
        quotation.quotation.user.name,
        quotation.quotation.user.email,
        quotation.quotation.brokerLicense,
      );

      dispatch(overwriteProfile(profile));
      dispatch(overwriteQuotation(quotation));
      dispatch(overwriteCreditorCatalog(creditorCatalog));
      dispatch(overwritePerson(person));
      dispatch(overwriteInspectionForm(inspectionForm));
      dispatch(overwriteCoverageCatalog(coverageCatalog));

      /**
       * We have to subtract it by one for failure routes, since we want in to be on Payment Page
       * instead of Summary Page.
       */
      dispatch(
        overwriteStepper({
          ...stepper,
          currentStepEmission: stepper.currentStepEmission - 1,
        }),
      );
      dispatch(overwriteEmissionPolicy(emissionPolicy));
      dispatch(overwriteCarPolicies(carPolicies));

      /**
       * We update EmissionStatusCode here because we don't want to call quotation endpoints again.
       */
      dispatch(updateEmissionStatusCode(RETRYPAYMENT));

      return dispatch(setRequestStatus(REQUEST_PERSISTED_DATA, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(REQUEST_PERSISTED_DATA, 'failure'));
    }
  };

/** Actions to take when payment fails. */
export const setYappyPaymentFailureState =
  (failureMessage: string): AsyncAppThunk =>
  async (dispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_FAILURE_STATUS, 'requesting'));
    dispatch(updateEmissionErrorCode(PAYMENT_FAILED));
    dispatch(setPaymentErrorCode(PAYMENT_FAILED));
    dispatch(setPaymentErrorDescription(failureMessage));
    trackEvent({
      eventName: SUMMARY_EMISSION_STEP,
      eventProperties: {
        [ERROR_CODE]: PAYMENT_FAILED,
        [ERROR_DESCRIPTION]: failureMessage,
      },
    });

    // Wait a bit for the status to update.
    await timeout(100);

    return dispatch(setRequestStatus(REQUEST_FAILURE_STATUS, 'success'));
  };

/**
 * If a quotation is still active, try to reconnect to the emission status check process.
 * It will restart the retrying request check logic.
 */
export const reconnectToEmittingQuotation =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    dispatch(setRequestStatus(CREATE_PRICING, 'requesting'));

    const { id } = getState().quotation.quotation;
    const uniqueId = getState().quotation.emissionLog?.emissionRequestId || '';

    try {
      await processRequest<EmissionPaymentStatus>({
        method: 'GET',
        url: `PolicyEmission/getCurrentStatus?quotationId=${id}&emissionRequestId=${uniqueId}`,
        retryProps: {
          maxAttempts: 10,
          customFail: incompleteStatus,
          customFatalFail: failedStatus,
        },
      });
      dispatch(updateEmissionErrorCode(''));
      return dispatch(setRequestStatus(CREATE_PRICING, 'success'));
    } catch (e) {
      handleCheckFailError(e, dispatch);
      return dispatch(setRequestStatus(CREATE_PRICING, 'failure'));
    }
  };

/**
 * Set exclusion date
 *
 * @param exclusionDate - Date
 */
export const setExclusionDate =
  (exclusionDate: string): AppThunk =>
  (dispatch): void => {
    dispatch(updateExclusionDate(exclusionDate));
  };

export const getEmission =
  (hashQuotation: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const { quotation } = getState().quotation;
    dispatch(setRequestStatus(REQUEST_CONSULT_EMISSION_DATA, 'requesting'));
    try {
      const response = await processRequest<
        NetworkResponse<EmissionConsulting>
      >({
        method: 'POST',
        url: `/PolicyEmission/saveEmission-byurlsalt`,
        data: {
          urlSalt: hashQuotation,
          quotationId: `${quotation.id}`,
          emissionRequestId: hashQuotation,
          lastUpdatedPage: 0,
        },
      });
      const emissionData = response.data;

      if (emissionData.salesEmissionRedux) {
        const {
          emissionPolicy,
          creditorCatalog,
          inspectionForm,
          carPolicies,
          legalEntityCatalog,
        } = emissionData.salesEmissionRedux;
        dispatch(overwriteEmissionPolicy(emissionPolicy));
        dispatch(overwriteCreditorCatalog(creditorCatalog));
        dispatch(overwriteInspectionForm(inspectionForm));
        dispatch(overwriteCarPolicies(carPolicies));
        if (legalEntityCatalog) {
          dispatch(overwriteLegalEntityCatalog(legalEntityCatalog));
        }
      }

      const { localSelectedPackageOption } = getState().quotation;
      const packageOption =
        localSelectedPackageOption > 0
          ? localSelectedPackageOption
          : emissionData.salesEmissionRequest.packageOption;

      // UPDATE EMISSION FORM STEP
      const { isStartedEmision } = getState().stepper;
      if (emissionData.lastUpdatedPage > 1) {
        dispatch(setIsOnPackageSelection(false));
        dispatch(setIsStartedEmission(true));
      }
      dispatch(updateCurrentStepEmission(emissionData.lastUpdatedPage - 1));

      if (emissionData.lastUpdatedPage === EMISSION_SIGNED) {
        dispatch(updateEmissionOrderId(emissionData.orderId));
        dispatch(setForcePaymentPage(true));
      }

      // UPDATE PACKAGE OPTION
      dispatch(setSelectedPackageOption(packageOption));
      // Force client to select option if option is invalid
      if (packageOption === 0 && isStartedEmision) {
        dispatch(setIsOnPackageSelection(true));
      }

      // RESET EVICERIA LINK
      dispatch(updateEvicertiaLink(''));

      return dispatch(
        setRequestStatus(REQUEST_CONSULT_EMISSION_DATA, 'success'),
      );
    } catch (e) {
      dispatch(updateEmissionErrorCode('error'));
      return dispatch(
        setRequestStatus(REQUEST_CONSULT_EMISSION_DATA, 'failure'),
      );
    }
  };

export const updateEmissionByPage =
  (lastUpdatedPage: number, orderId = ''): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const currentState = getState();
    const { quotation } = currentState.quotation;
    const { emissionData } = makeYappyPaymentPayload(currentState);
    const { salesEmissionRedux, salesEmissionRequest } = emissionData;

    const saveRequest = {
      orderId,
      quotationId: quotation.id,
      urlSalt: quotation.urlSalt,
      emissionRequestId: quotation.urlSalt,
      salesEmissionRedux,
      salesEmissionRequest,
      lastUpdatedPage,
    };

    // Set request status to 'requesting'
    dispatch(setRequestStatus(REQUEST_UPDATE_EMISSION_DATA, 'requesting'));
    // Perform the network request
    try {
      await processRequest({
        method: 'PUT',
        url: 'PolicyEmission/updateEmission-byurlsalt',
        data: saveRequest,
      });

      dispatch(updateEmissionErrorCode(''));
      return dispatch(
        setRequestStatus(REQUEST_UPDATE_EMISSION_DATA, 'success'),
      );
    } catch (e) {
      handleCheckFailError(e, dispatch);
      return dispatch(
        setRequestStatus(REQUEST_UPDATE_EMISSION_DATA, 'failure'),
      );
    }
  };

export const requestFractionation =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const currentState = getState();
    const { quotation } = currentState.quotation;

    const selectecUnit =
      quotation.units[getIndexFromPackageOption(quotation.packageOption)];

    const fractionationPayload = {
      quotationId: quotation.id,
      ammountToInstallments: selectecUnit.total.grandTotal,
    };

    // Set request status to 'requesting'
    dispatch(setRequestStatus(REQUEST_FRACTIONATION, 'requesting'));

    // Perform the network request
    try {
      const response = await processRequest<NetworkResponse<Fraction[]>>({
        method: 'POST',
        url: '/PolicyEmission/payment-fractions',
        data: fractionationPayload,
      });

      dispatch(updateFractionation(response.data));
      return dispatch(setRequestStatus(REQUEST_FRACTIONATION, 'success'));
    } catch (e) {
      handleCheckFailError(e, dispatch);
      return dispatch(setRequestStatus(REQUEST_FRACTIONATION, 'failure'));
    }
  };

/**
 * Set legal beneficiaries
 *
 * @param beneficiaries - LegalBeneficiary - new beneficiaries
 */
export const setLegalBeneficiaries =
  (beneficiaries: LegalBeneficiary[]): AppThunk =>
  (dispatch): void => {
    dispatch(updateLegalBeneficiaries(beneficiaries));
  };

export const requestUpselling =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const currentState = getState();
    const { quotation } = currentState.quotation;

    const { urlSalt } = quotation;
    const { packageOption } = quotation;

    const urlOption = `/Quotation/${urlSalt}/upsell?packageOption=${packageOption.toString()}&upSellEndorsement=0049`;

    // Set request status to 'requesting'
    dispatch(setRequestStatus(REQUEST_UPSELLING, 'requesting'));

    // Perform the network request
    try {
      await processRequest({
        method: 'PUT',
        url: urlOption,
      });

      return dispatch(setRequestStatus(REQUEST_UPSELLING, 'success'));
    } catch (e) {
      handleCheckFailError(e, dispatch);
      return dispatch(setRequestStatus(REQUEST_UPSELLING, 'failure'));
    }
  };
